import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../ui/Header';
import Sidebar from '../../ui/Sidebar';
import RoleSelector from './components/RoleSelector';
import WelcomeHeader from './components/WelcomeHeader';
import QuickActions from './components/QuickActions';
import MetricsGrid from './components/MetricsGrid';
import ActivityFeed from './components/ActivityFeed';
import PerformanceChart from './components/PerformanceChart';
import OpportunityCard from './components/OpportunityCard';
import NotificationPanel from './components/NotificationPanel';
import Icon from '../../AppIcon';
import Button from '../../ui/Button';

const MultiRoleDashboard = () => {
  const [currentRole, setCurrentRole] = useState('corporate');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');

  // Mock user data
  const user = {
    name: "Sarah Johnson",
    email: "sarah.johnson@innovatecorp.com",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae7d9bdc-1762274136565.png",
    avatarAlt: "Professional headshot of woman with shoulder-length brown hair in navy blazer"
  };

  // Available roles
  const availableRoles = [
  { id: 'corporate', name: 'Corporate', icon: 'Building2' },
  { id: 'startup', name: 'Startup', icon: 'Rocket' },
  { id: 'intermediary', name: 'Intermediary', icon: 'Users' },
  { id: 'field', name: 'Field Staff', icon: 'MapPin' }];


  const currentRoleData = availableRoles?.find((role) => role?.id === currentRole);

  // Role-specific data
  const getRoleSpecificData = () => {
    switch (currentRole) {
      case 'corporate':
        return {
          welcomeStats: [
          { value: '12', label: 'Active Programs' },
          { value: '89', label: 'Matched Startups' },
          { value: '94%', label: 'Success Rate' }],

          metrics: [
          {
            icon: 'Briefcase',
            value: '12',
            label: 'Active Programs',
            subtitle: '3 new this month',
            trend: 15,
            bgColor: 'bg-primary/10',
            iconColor: 'text-primary'
          },
          {
            icon: 'Users',
            value: '89',
            label: 'Matched Startups',
            subtitle: 'Across all programs',
            trend: 8,
            bgColor: 'bg-success/10',
            iconColor: 'text-success'
          },
          {
            icon: 'TrendingUp',
            value: '$2.4M',
            label: 'Investment Pipeline',
            subtitle: 'Potential value',
            trend: 23,
            bgColor: 'bg-accent/10',
            iconColor: 'text-accent'
          },
          {
            icon: 'Target',
            value: '94%',
            label: 'Match Success Rate',
            subtitle: 'Last 30 days',
            trend: 5,
            bgColor: 'bg-warning/10',
            iconColor: 'text-warning'
          }],

          quickActions: [
          {
            title: 'Create Program',
            description: 'Launch new scouting program',
            icon: 'Plus',
            variant: 'default',
            path: '/program-discovery-engine'
          },
          {
            title: 'Review Matches',
            description: 'Evaluate startup recommendations',
            icon: 'Eye',
            variant: 'outline',
            onClick: () => console.log('Review matches')
          },
          {
            title: 'Analytics Dashboard',
            description: 'View detailed insights',
            icon: 'BarChart3',
            variant: 'outline',
            path: '/analytics-insights-center'
          },
          {
            title: 'Manage Team',
            description: 'Add team members',
            icon: 'UserPlus',
            variant: 'outline',
            onClick: () => console.log('Manage team')
          },
          {
            title: 'Export Reports',
            description: 'Download performance data',
            icon: 'Download',
            variant: 'outline',
            onClick: () => console.log('Export reports')
          },
          {
            title: 'Settings',
            description: 'Configure preferences',
            icon: 'Settings',
            variant: 'outline',
            onClick: () => console.log('Settings')
          }]

        };

      case 'startup':
        return {
          welcomeStats: [
          { value: '8', label: 'Applications' },
          { value: '3', label: 'Interviews' },
          { value: '67%', label: 'Match Rate' }],

          metrics: [
          {
            icon: 'FileText',
            value: '8',
            label: 'Active Applications',
            subtitle: '2 pending review',
            trend: 12,
            bgColor: 'bg-primary/10',
            iconColor: 'text-primary'
          },
          {
            icon: 'Calendar',
            value: '3',
            label: 'Scheduled Interviews',
            subtitle: 'Next: Tomorrow 2PM',
            trend: 50,
            bgColor: 'bg-success/10',
            iconColor: 'text-success'
          },
          {
            icon: 'DollarSign',
            value: '$450K',
            label: 'Funding Potential',
            subtitle: 'From active programs',
            trend: 18,
            bgColor: 'bg-accent/10',
            iconColor: 'text-accent'
          },
          {
            icon: 'Star',
            value: '4.8',
            label: 'Profile Rating',
            subtitle: 'Based on 12 reviews',
            trend: 3,
            bgColor: 'bg-warning/10',
            iconColor: 'text-warning'
          }],

          quickActions: [
          {
            title: 'Browse Programs',
            description: 'Find new opportunities',
            icon: 'Search',
            variant: 'default',
            path: '/program-discovery-engine'
          },
          {
            title: 'Update Profile',
            description: 'Enhance your visibility',
            icon: 'User',
            variant: 'outline',
            onClick: () => console.log('Update profile')
          },
          {
            title: 'Track Applications',
            description: 'Monitor progress',
            icon: 'FileText',
            variant: 'outline',
            onClick: () => console.log('Track applications')
          },
          {
            title: 'Schedule Demo',
            description: 'Book presentation slot',
            icon: 'Calendar',
            variant: 'outline',
            onClick: () => console.log('Schedule demo')
          },
          {
            title: 'Resources',
            description: 'Access startup guides',
            icon: 'BookOpen',
            variant: 'outline',
            onClick: () => console.log('Resources')
          },
          {
            title: 'Network',
            description: 'Connect with peers',
            icon: 'Users',
            variant: 'outline',
            onClick: () => console.log('Network')
          }]

        };

      case 'intermediary':
        return {
          welcomeStats: [
          { value: '24', label: 'Referrals' },
          { value: '$8.2K', label: 'Earnings' },
          { value: '78%', label: 'Success Rate' }],

          metrics: [
          {
            icon: 'UserPlus',
            value: '24',
            label: 'Active Referrals',
            subtitle: '6 pending verification',
            trend: 20,
            bgColor: 'bg-primary/10',
            iconColor: 'text-primary'
          },
          {
            icon: 'DollarSign',
            value: '$8,240',
            label: 'Total Earnings',
            subtitle: 'This month: $1,850',
            trend: 35,
            bgColor: 'bg-success/10',
            iconColor: 'text-success'
          },
          {
            icon: 'Target',
            value: '78%',
            label: 'Success Rate',
            subtitle: 'Above network average',
            trend: 8,
            bgColor: 'bg-accent/10',
            iconColor: 'text-accent'
          },
          {
            icon: 'Network',
            value: '156',
            label: 'Network Size',
            subtitle: 'Verified connections',
            trend: 12,
            bgColor: 'bg-warning/10',
            iconColor: 'text-warning'
          }],

          quickActions: [
          {
            title: 'Submit Referral',
            description: 'Add new startup referral',
            icon: 'Plus',
            variant: 'default',
            path: '/real-time-scouting-interface'
          },
          {
            title: 'Track Performance',
            description: 'View earnings & metrics',
            icon: 'TrendingUp',
            variant: 'outline',
            path: '/referral-performance-center'
          },
          {
            title: 'Browse Programs',
            description: 'Find new opportunities',
            icon: 'Search',
            variant: 'outline',
            path: '/program-discovery-engine'
          },
          {
            title: 'Expand Network',
            description: 'Connect with startups',
            icon: 'Users',
            variant: 'outline',
            onClick: () => console.log('Expand network')
          },
          {
            title: 'Training Center',
            description: 'Improve your skills',
            icon: 'GraduationCap',
            variant: 'outline',
            onClick: () => console.log('Training center')
          },
          {
            title: 'Payout History',
            description: 'View payment records',
            icon: 'CreditCard',
            variant: 'outline',
            onClick: () => console.log('Payout history')
          }]

        };

      case 'field':
        return {
          welcomeStats: [
          { value: '18', label: 'Scouts Today' },
          { value: '92%', label: 'Data Quality' },
          { value: '5.2', label: 'Avg Rating' }],

          metrics: [
          {
            icon: 'MapPin',
            value: '18',
            label: 'Scouts Completed',
            subtitle: 'Today: 6 verified',
            trend: 15,
            bgColor: 'bg-primary/10',
            iconColor: 'text-primary'
          },
          {
            icon: 'Shield',
            value: '92%',
            label: 'Data Quality Score',
            subtitle: 'Above team average',
            trend: 5,
            bgColor: 'bg-success/10',
            iconColor: 'text-success'
          },
          {
            icon: 'Clock',
            value: '4.2h',
            label: 'Avg Response Time',
            subtitle: 'Target: &lt;6h',
            trend: -12,
            bgColor: 'bg-accent/10',
            iconColor: 'text-accent'
          },
          {
            icon: 'Star',
            value: '5.2',
            label: 'Performance Rating',
            subtitle: 'Based on 45 reviews',
            trend: 8,
            bgColor: 'bg-warning/10',
            iconColor: 'text-warning'
          }],

          quickActions: [
          {
            title: 'Start Scouting',
            description: 'Begin field operations',
            icon: 'Play',
            variant: 'default',
            path: '/real-time-scouting-interface'
          },
          {
            title: 'View Territory',
            description: 'Check assigned areas',
            icon: 'Map',
            variant: 'outline',
            onClick: () => console.log('View territory')
          },
          {
            title: 'Submit Report',
            description: 'Upload findings',
            icon: 'Upload',
            variant: 'outline',
            onClick: () => console.log('Submit report')
          },
          {
            title: 'Sync Data',
            description: 'Update offline records',
            icon: 'RefreshCw',
            variant: 'outline',
            onClick: () => console.log('Sync data')
          },
          {
            title: 'Training Materials',
            description: 'Access field guides',
            icon: 'BookOpen',
            variant: 'outline',
            onClick: () => console.log('Training materials')
          },
          {
            title: 'Contact Support',
            description: 'Get assistance',
            icon: 'HelpCircle',
            variant: 'outline',
            onClick: () => console.log('Contact support')
          }]

        };

      default:
        return {
          welcomeStats: [],
          metrics: [],
          quickActions: []
        };
    }
  };

  const roleData = getRoleSpecificData();

  // Mock activity data
  const activities = [
  {
    type: 'match',
    title: 'New Startup Match',
    description: 'TechFlow AI matched with Innovation Program #12',
    timestamp: new Date(Date.now() - 300000),
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_176bc4cf0-1762273327893.png",
    avatarAlt: 'Professional headshot of young man with dark hair in white shirt',
    status: 'success'
  },
  {
    type: 'application',
    title: 'Application Submitted',
    description: 'GreenTech Solutions applied to Sustainability Challenge',
    timestamp: new Date(Date.now() - 1800000),
    avatar: "https://images.unsplash.com/photo-1688597628916-d3230d8ac41e",
    avatarAlt: 'Professional headshot of woman with blonde hair in black blazer',
    status: 'pending'
  },
  {
    type: 'referral',
    title: 'Referral Verified',
    description: 'DataSync startup referral approved by verification team',
    timestamp: new Date(Date.now() - 3600000),
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc680449-1762273376990.png",
    avatarAlt: 'Professional headshot of man with beard in navy suit',
    status: 'success'
  },
  {
    type: 'program',
    title: 'Program Updated',
    description: 'AI Innovation Challenge requirements modified',
    timestamp: new Date(Date.now() - 7200000),
    status: 'pending'
  },
  {
    type: 'payment',
    title: 'Commission Paid',
    description: '$450 referral commission processed successfully',
    timestamp: new Date(Date.now() - 10800000),
    status: 'success'
  }];


  // Mock opportunities data
  const opportunities = [
  {
    title: 'AI Innovation Challenge',
    company: 'TechCorp Industries',
    description: 'Seeking AI startups for enterprise automation solutions with focus on machine learning and natural language processing capabilities.',
    budget: '$500K - $2M',
    deadline: 'Dec 15, 2024',
    location: 'San Francisco, CA',
    priority: 'high',
    status: 'active',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c21afe9c-1763103482760.png",
    logoAlt: 'Modern tech company logo with blue and white geometric design',
    tags: ['AI', 'Machine Learning', 'Enterprise', 'SaaS']
  },
  {
    title: 'Sustainability Tech Program',
    company: 'GreenFuture Corp',
    description: 'Looking for clean technology startups focused on renewable energy, waste reduction, and carbon footprint optimization.',
    budget: '$250K - $1M',
    deadline: 'Jan 20, 2025',
    location: 'Austin, TX',
    priority: 'medium',
    status: 'active',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ebc445a6-1763103483129.png",
    logoAlt: 'Green energy company logo with leaf and solar panel elements',
    tags: ['CleanTech', 'Renewable Energy', 'Sustainability']
  },
  {
    title: 'FinTech Accelerator',
    company: 'Banking Solutions Inc',
    description: 'Accelerator program for financial technology startups specializing in blockchain, digital payments, and financial inclusion.',
    budget: '$100K - $500K',
    deadline: 'Nov 30, 2024',
    location: 'New York, NY',
    priority: 'low',
    status: 'pending',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_17aa7928b-1763103485421.png",
    logoAlt: 'Financial services company logo with gold and navy blue colors',
    tags: ['FinTech', 'Blockchain', 'Digital Payments']
  }];


  // Mock notifications
  const notifications = [
  {
    type: 'success',
    title: 'Application Approved',
    message: 'Your startup application for AI Innovation Challenge has been approved.',
    time: '2 min ago',
    read: false,
    actions: [
    { label: 'View Details', variant: 'outline', onClick: () => console.log('View details') },
    { label: 'Schedule Interview', variant: 'default', onClick: () => console.log('Schedule') }]

  },
  {
    type: 'warning',
    title: 'Profile Incomplete',
    message: 'Please complete your startup profile to improve match accuracy.',
    time: '1 hour ago',
    read: false,
    actions: [
    { label: 'Complete Profile', variant: 'default', onClick: () => console.log('Complete profile') }]

  },
  {
    type: 'info',
    title: 'New Program Available',
    message: 'HealthTech Innovation Program is now accepting applications.',
    time: '3 hours ago',
    read: true
  },
  {
    type: 'error',
    title: 'Verification Failed',
    message: 'Document verification failed. Please resubmit required documents.',
    time: '1 day ago',
    read: true,
    actions: [
    { label: 'Resubmit', variant: 'default', onClick: () => console.log('Resubmit') }]

  }];


  // Mock chart data
  const performanceData = [
  { name: 'Mon', value: 12 },
  { name: 'Tue', value: 19 },
  { name: 'Wed', value: 15 },
  { name: 'Thu', value: 25 },
  { name: 'Fri', value: 22 },
  { name: 'Sat', value: 18 },
  { name: 'Sun', value: 16 }];


  const revenueData = [
  { name: 'Jan', value: 4500 },
  { name: 'Feb', value: 5200 },
  { name: 'Mar', value: 4800 },
  { name: 'Apr', value: 6100 },
  { name: 'May', value: 5900 },
  { name: 'Jun', value: 7200 }];


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className={`transition-all duration-300 ${
      sidebarCollapsed ? 'ml-16' : 'ml-64'} pt-16`
      }>
        <div className="p-6 space-y-6">
          {/* Role Selector */}
          <RoleSelector
            currentRole={currentRole}
            onRoleChange={setCurrentRole}
            availableRoles={availableRoles} />


          {/* Welcome Header */}
          <WelcomeHeader
            user={user}
            currentRole={currentRoleData}
            stats={roleData?.welcomeStats} />


          {/* Metrics Grid */}
          <MetricsGrid metrics={roleData?.metrics} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Quick Actions & Performance */}
            <div className="lg:col-span-2 space-y-6">
              <QuickActions actions={roleData?.quickActions} />
              
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <PerformanceChart
                  title="Weekly Performance"
                  data={performanceData}
                  type="line"
                  color="var(--color-primary)" />

                <PerformanceChart
                  title="Monthly Revenue"
                  data={revenueData}
                  type="bar"
                  color="var(--color-accent)" />

              </div>

              {/* Opportunities Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">
                    {currentRole === 'corporate' ? 'Recent Programs' :
                    currentRole === 'startup' ? 'Recommended Opportunities' :
                    currentRole === 'intermediary' ? 'Available Programs' : 'Assigned Territories'}
                  </h3>
                  <Link to="/program-discovery-engine">
                    <Button variant="outline" size="sm">
                      <Icon name="ArrowRight" size={16} className="mr-2" />
                      View All
                    </Button>
                  </Link>
                </div>
                
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {opportunities?.slice(0, 2)?.map((opportunity, index) =>
                  <OpportunityCard key={index} opportunity={opportunity} />
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Activity & Notifications */}
            <div className="space-y-6">
              <ActivityFeed activities={activities} />
              <NotificationPanel notifications={notifications} />
            </div>
          </div>

          {/* Bottom Section - Additional Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Quick Stats</h3>
                <Icon name="BarChart3" size={20} className="text-muted-foreground" />
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Platform Engagement</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="w-3/4 h-full bg-primary rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium">75%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Goal Completion</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="w-4/5 h-full bg-success rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium">80%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Network Growth</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="w-3/5 h-full bg-accent rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium">60%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">System Status</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span className="text-sm text-success">All Systems Operational</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">API Response Time</span>
                  <span className="text-sm font-medium text-success">142ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Database Status</span>
                  <span className="text-sm font-medium text-success">Healthy</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active Users</span>
                  <span className="text-sm font-medium text-foreground">2,847</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Server Load</span>
                  <span className="text-sm font-medium text-warning">68%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default MultiRoleDashboard;